import { createBrowserRouter, type RouteObject } from 'react-router-dom'
import { ConsolePage } from './pages/ConsolePage'
import { SettingsPage } from './pages/SettingsPage'
import { HistoryPage } from './pages/HistoryPage'
import { TranscriptPage } from './pages/TranscriptPage'
import { MediaPage } from './pages/MediaPage'
import { SignInPage } from './pages/SignInPage'
import { RequireAuth, RedirectIfSignedIn } from './components/auth/AuthGate'

/**
 * The four store-`page`-backed routes (`null | 'settings' | 'history' |
 * 'transcript'`, see src/store.ts and docs/design-handoff/README.md "State
 * management") plus `/sign-in` (task 31 — staff sign-in has no `page` value
 * of its own; it is gated by `authStatus`, a separate axis, not the
 * settings/history/transcript navigation `page` models) plus `/media/:id`
 * (task 8, media images — also no `page` value of its own; it is a deep-link
 * destination, not a console-navigation surface, exactly like `/transcript/:id`).
 *
 * Every store-`page` route is wrapped in `RequireAuth`
 * (src/components/auth/AuthGate.tsx): an unauthenticated visitor is
 * redirected to `/sign-in` instead of ever mounting the real page.
 * `/sign-in` itself is wrapped in the inverse `RedirectIfSignedIn`, so an
 * already-signed-in visitor who lands there is sent straight through to `/`.
 *
 * `/users` USED TO carry a second wrapper here, `RequireAdmin`, on top of
 * `RequireAuth` (users-and-permissions design §6). Moved 2026-08-10 into
 * Settings as a pane (`components/settings/UsersSection.tsx`) — it is staff
 * configuration, so it belongs with the rest of configuration rather than
 * its own route. `RequireAdmin` had exactly one caller and is deleted with
 * it; the admin gate that mattered — never showing a non-admin the real
 * staff list — is now `UsersSection`'s own early return, in the same place
 * every other settings section already decides what a non-admin may see.
 *
 * Exported separately from `createAppRouter` so tests can render them with
 * `createMemoryRouter` at any path without touching the browser history API.
 */
export const routes: RouteObject[] = [
  {
    path: '/sign-in',
    element: (
      <RedirectIfSignedIn>
        <SignInPage />
      </RedirectIfSignedIn>
    ),
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <ConsolePage />
      </RequireAuth>
    ),
  },
  {
    path: '/settings',
    element: (
      <RequireAuth>
        <SettingsPage />
      </RequireAuth>
    ),
  },
  {
    path: '/history',
    element: (
      <RequireAuth>
        <HistoryPage />
      </RequireAuth>
    ),
  },
  {
    path: '/transcript/:id',
    element: (
      <RequireAuth>
        <TranscriptPage />
      </RequireAuth>
    ),
  },
  {
    // Task 8 (media images): the signed-in full-size view a Halo note's
    // image line links back to. Gated by the same `RequireAuth` wrapper as
    // every other content route here — see MediaPage.tsx's own header
    // comment.
    path: '/media/:id',
    element: (
      <RequireAuth>
        <MediaPage />
      </RequireAuth>
    ),
  },
]

export function createAppRouter() {
  return createBrowserRouter(routes)
}
